import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';

const ASSET_TYPES = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'drill_rig', label: 'Drill Rig' },
  { value: 'other', label: 'Other' },
];

function AssetModal({ asset, onSave, onCancel }) {
  const [name, setName] = useState(asset?.name ?? '');
  const [type, setType] = useState(asset?.type ?? 'vehicle');
  const [identifier, setIdentifier] = useState(asset?.identifier ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    await onSave({ name: name.trim(), type, identifier: identifier.trim() || null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-sm space-y-4">
        <h3 className="text-white font-semibold text-lg">{asset ? 'Edit Asset' : 'Add Asset'}</h3>

        {error && (
          <p className="text-red-400 text-sm bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
        )}

        <div>
          <label className="block text-gray-400 text-xs mb-1.5">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="e.g. Hilux 1, Rig 3"
          />
        </div>

        <div>
          <label className="block text-gray-400 text-xs mb-1.5">Type *</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {ASSET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 text-xs mb-1.5">Rego / Identifier</label>
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            maxLength={50}
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="e.g. 1ABC234"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-xl text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl text-sm"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAssets() {
  const { membership } = useOrg();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | asset object
  const [error, setError] = useState('');

  const fetchAssets = async () => {
    const { data } = await supabase
      .from('assets')
      .select('*')
      .eq('org_id', membership.org_id)
      .order('name');
    if (data) setAssets(data);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (membership?.org_id) fetchAssets();
  }, [membership?.org_id]);

  const handleSave = async (fields) => {
    setError('');
    if (modal === 'add') {
      const { error: e } = await supabase
        .from('assets')
        .insert({ ...fields, org_id: membership.org_id });
      if (e) { setError(e.message); return; }
    } else {
      const { error: e } = await supabase
        .from('assets')
        .update(fields)
        .eq('id', modal.id);
      if (e) { setError(e.message); return; }
    }
    setModal(null);
    fetchAssets();
  };

  const toggleActive = async (asset) => {
    await supabase
      .from('assets')
      .update({ active: !asset.active })
      .eq('id', asset.id);
    fetchAssets();
  };

  const typeLabels = { vehicle: 'Vehicle', drill_rig: 'Drill Rig', other: 'Other' };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header title="Assets" backTo="/admin" />

      {modal && (
        <AssetModal
          asset={modal === 'add' ? null : modal}
          onSave={handleSave}
          onCancel={() => setModal(null)}
        />
      )}

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {error && (
          <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 border border-red-500/30">
            {error}
          </div>
        )}

        <button
          onClick={() => setModal('add')}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors"
        >
          + Add Asset
        </button>

        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : assets.length === 0 ? (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
            <p className="text-gray-500">No assets yet. Add a vehicle or rig above.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className={`bg-gray-800 border rounded-xl p-4 ${
                  asset.active ? 'border-gray-700' : 'border-gray-700/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{asset.name}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {typeLabels[asset.type] || asset.type}
                      {asset.identifier && ` · ${asset.identifier}`}
                    </p>
                    {!asset.active && (
                      <span className="text-xs text-gray-600 mt-0.5 block">Inactive</span>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0 ml-3">
                    <button
                      onClick={() => setModal(asset)}
                      className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded-lg bg-gray-700 hover:bg-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(asset)}
                      className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded-lg bg-gray-700 hover:bg-gray-600"
                    >
                      {asset.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
